import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Heart, Clock, Flame, Coins, PlusCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchActiveQuests, type Quest } from '@/lib/api';

export function ActiveQuests() {
  const { t, i18n } = useTranslation();
  const [quests, setQuests] = useState<Quest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchActiveQuests({ limit: 5, lang: i18n.language.slice(0, 2) })
      .then(setQuests)
      .catch(() => setQuests([]))
      .finally(() => setLoading(false));
  }, [i18n.language]);

  return (
    <section className="section">
      <div className="container-narrow">
        <div className="text-center mb-16">
          <h2 className="font-pixel text-4xl sm:text-5xl font-bold tracking-tight">
            {t('active_quests.title')}
          </h2>
          <p className="mt-4 text-kquest-300 text-lg">{t('active_quests.subtitle')}</p>
        </div>

        {loading ? (
          <div className="text-center text-kquest-300 font-pixel py-8">Loading…</div>
        ) : quests.length === 0 ? (
          <EmptyActive />
        ) : (
          <div className="grid gap-4">
            {quests.map((q, i) => {
              const totalEur = (q.paid_amount_cents / 100).toFixed(2);
              const isVetoed = !!q.vetoed_by_user_id;
              return (
                <motion.article
                  key={q.id}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.4, delay: i * 0.07 }}
                  className={
                    'kblock p-6 flex flex-col sm:flex-row gap-4 sm:items-center enchant-card ' +
                    (isVetoed ? 'border-violet-400 shadow-[0_0_24px_rgba(168,85,247,0.45)]' : '')
                  }
                >
                  {/* Rank chip */}
                  <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center font-press text-lg bg-kquest-700 border-2 border-kquest-500 text-gold-400"
                       style={{ boxShadow: 'inset 0 -3px 0 0 rgba(0,0,0,0.4), inset 0 3px 0 0 rgba(255,255,255,0.08)' }}>
                    {i + 1}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {isVetoed && (
                        <span className="pill-pixel bg-violet-500/20 border-violet-400/60 text-violet-200">
                          👑 {t('active_quests.king_veto', { defaultValue: 'Pushata da un Re' })}
                        </span>
                      )}
                      {q.is_hot && (
                        <span className="pill-pixel bg-gold-500/15 border-gold-500/40 text-gold-300">
                          <Flame className="w-3 h-3" /> Hot
                        </span>
                      )}
                      <span className="text-xs text-kquest-400 font-pixel">@{q.author_username}</span>
                    </div>
                    <h3 className="font-pixel text-xl font-semibold leading-tight text-kquest-100">
                      {q.title}
                    </h3>
                    <div className="mt-3 flex items-center gap-4 text-sm text-kquest-300 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Heart className="w-4 h-4 text-gold-400" /> {q.vote_count} {t('active_quests.votes', { defaultValue: 'voti' })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Coins className="w-4 h-4 text-xp-400" /> €{totalEur}
                      </span>
                      {q.days_left !== undefined && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" /> {q.days_left}g
                        </span>
                      )}
                    </div>
                  </div>

                  <button className="btn-purple !px-5 !py-3 self-start sm:self-auto">
                    +1 voto · €0,50
                  </button>
                </motion.article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function EmptyActive() {
  const { t } = useTranslation();
  return (
    <div className="kblock p-12 max-w-2xl mx-auto text-center">
      <PlusCircle className="w-16 h-16 text-violet-400 mx-auto mb-6 opacity-80" />
      <h3 className="font-pixel text-2xl mb-3">
        {t('active_quests.empty_title', { defaultValue: 'Nessuna quest attiva' })}
      </h3>
      <p className="text-kquest-300 mb-6 leading-relaxed">
        {t('active_quests.empty_body', {
          defaultValue: 'Sii il primo a proporre una quest. Costa €5 (o gratis se sei abbonato).',
        })}
      </p>
      <Link to="/submit" className="btn-gold !px-6 !py-3 inline-flex items-center gap-2">
        <PlusCircle className="w-4 h-4" />
        {t('active_quests.empty_cta', { defaultValue: 'Proponi una quest' })}
      </Link>
    </div>
  );
}
